import React from "react";
import { Button, DatePicker, InputNumber, Popover, Radio, Space, Typography } from "@arco-design/web-react";
import { IconCalendar } from "@arco-design/web-react/icon";
import { DATE_OPS, DATE_PRESETS, dateText, describeDateRange, isDateComplete } from "./contactFilter";

const { Text } = Typography;

// Picks a date condition step by step: first what kind (has a date, in the
// last, between, …); then, for "in the last", a number of days or a preset,
// or for "between", two dates; then the date or range it means right now, and
// Apply. Dates are in UTC, as the server measures them.
export function DateConditionPicker({ value, onApply, onCancel, now }) {
  const [cond, setCond] = React.useState(value || { op: "" });
  const set = (patch) => setCond((c) => ({ ...c, ...patch }));

  const pickOp = (op) => {
    // Keep what was entered when switching between matching kinds
    // (in the last ↔ not in the last, between ↔ not between).
    const sameKind = (a, b) => a.replace("not_", "") === b.replace("not_", "");
    setCond((c) => (sameKind(c.op || "", op) ? { ...c, op } : { op, days: op.endsWith("in_last") ? 30 : undefined }));
  };

  const rolling = cond.op === "in_last" || cond.op === "not_in_last";
  const range = cond.op === "between" || cond.op === "not_between";
  const complete = isDateComplete(cond);

  let preview = "";
  if (complete) {
    const span = describeDateRange(cond, now);
    switch (cond.op) {
      case "exists": preview = "Any contact with this date set"; break;
      case "not_exists": preview = "Contacts without this date"; break;
      case "in_last": preview = span; break;
      case "not_in_last": preview = span.replace(/^Since /, "Before ") + ", or no date"; break;
      case "between": preview = span; break;
      default: preview = "Outside " + span + ", or no date";
    }
  }

  return (
    <div style={{ width: 300 }} data-testid="date-condition-picker">
      <Radio.Group direction="vertical" value={cond.op} onChange={pickOp}>
        {DATE_OPS.map(([op, label]) => (
          <Radio key={op} value={op}>{label}</Radio>
        ))}
      </Radio.Group>

      {rolling && (
        <div style={{ marginTop: 12 }}>
          <Space>
            <InputNumber
              aria-label="Days"
              min={1}
              max={36500}
              style={{ width: 110 }}
              value={cond.period ? undefined : cond.days}
              placeholder="N"
              onChange={(n) => set({ days: n, period: undefined })}
            />
            <Text>days</Text>
          </Space>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {DATE_PRESETS.map((p) => {
              const on = p.period ? cond.period === p.period : !cond.period && cond.days === p.days;
              return (
                <Button
                  key={p.label}
                  size="mini"
                  type={on ? "primary" : "secondary"}
                  onClick={() => set(p.period ? { period: p.period, days: undefined } : { days: p.days, period: undefined })}
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {range && (
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <DatePicker
            placeholder="From"
            value={cond.from}
            onChange={(ds) => set({ from: ds || undefined })}
            style={{ flex: 1 }}
          />
          <DatePicker
            placeholder="To"
            value={cond.to}
            onChange={(ds) => set({ to: ds || undefined })}
            style={{ flex: 1 }}
          />
        </div>
      )}
      {range && cond.from && cond.to && cond.from > cond.to && (
        <Text type="error" style={{ display: "block", marginTop: 4, fontSize: 12 }}>The end date is before the start date.</Text>
      )}

      {preview && (
        <div data-testid="date-preview" style={{ marginTop: 12, padding: "6px 8px", background: "var(--color-fill-2)", borderRadius: 4, fontSize: 13 }}>
          {preview}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        {onCancel && <Button size="small" onClick={onCancel}>Cancel</Button>}
        <Button size="small" type="primary" disabled={!complete} onClick={() => onApply(cond)}>Apply</Button>
      </div>
    </div>
  );
}

// DateConditionButton shows a term's date condition and opens the picker.
export default function DateConditionButton({ value, onChange }) {
  const [open, setOpen] = React.useState(!value);
  return (
    <Popover
      trigger="click"
      position="bl"
      popupVisible={open}
      onVisibleChange={setOpen}
      content={
        open && (
          <DateConditionPicker
            value={value}
            onApply={(c) => { onChange(c); setOpen(false); }}
            onCancel={() => setOpen(false)}
          />
        )
      }
    >
      <Button size="small" icon={<IconCalendar />}>{value ? dateText(value) : "Choose dates…"}</Button>
    </Popover>
  );
}
